/**
 * The admin client (SPEC §10).
 *
 * Built on `request` from `lib/api.ts`, so the §10 envelope and the CSRF
 * header stay in one place. Nothing here retries and nothing swallows a
 * failure: an admin form that silently does not save is worse than one that
 * says it could not.
 */

import { request } from './api'
import type {
  AdminReportRow,
  AdminUserDetail,
  AdminUserSummary,
  AuditFilters,
  AuditPage,
  CachePurgeResult,
  CacheStats,
  DepartmentCreate,
  DepartmentPatch,
  DepartmentRow,
  GrantInput,
  ReloadResult,
  Role,
  UserCreate,
} from './adminTypes'

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value))
  }
  const rendered = search.toString()
  return rendered === '' ? '' : `?${rendered}`
}

export const adminApi = {
  users: (
    filters: { q?: string | undefined; department?: string | undefined } = {},
  ): Promise<AdminUserSummary[]> =>
    request<AdminUserSummary[]>(`/api/admin/users${query(filters)}`),

  user: (id: string): Promise<AdminUserDetail> =>
    request<AdminUserDetail>(`/api/admin/users/${encodeURIComponent(id)}`),

  createUser: (payload: UserCreate): Promise<AdminUserDetail> =>
    request<AdminUserDetail>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  patchUser: (
    id: string,
    patch: { is_active?: boolean; is_admin?: boolean },
  ): Promise<AdminUserDetail> =>
    request<AdminUserDetail>(`/api/admin/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteUser: async (id: string): Promise<void> => {
    await request<undefined>(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' })
  },

  /** Wholesale replacement, like grants (§10). */
  setUserRoles: (id: string, roleIds: readonly number[]): Promise<AdminUserDetail> =>
    request<AdminUserDetail>(`/api/admin/users/${encodeURIComponent(id)}/roles`, {
      method: 'PUT',
      body: JSON.stringify({ role_ids: roleIds }),
    }),

  setUserDepartmentAccess: (
    id: string,
    departmentKeys: readonly string[],
  ): Promise<AdminUserDetail> =>
    request<AdminUserDetail>(
      `/api/admin/users/${encodeURIComponent(id)}/department-access`,
      {
        method: 'PUT',
        body: JSON.stringify({ department_keys: departmentKeys }),
      },
    ),

  roles: (): Promise<Role[]> => request<Role[]>('/api/admin/roles'),

  createRole: (payload: { key: string; name: string; description?: string }): Promise<Role> =>
    request<Role>('/api/admin/roles', { method: 'POST', body: JSON.stringify(payload) }),

  patchRole: (id: number, patch: { name?: string; description?: string }): Promise<Role> =>
    request<Role>(`/api/admin/roles/${String(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteRole: async (id: number): Promise<void> => {
    // The route answers 204, which `request` returns as undefined.
    await request<undefined>(`/api/admin/roles/${String(id)}`, { method: 'DELETE' })
  },

  /**
   * Full replacement. The server refuses `all` carrying slugs and `selected`
   * with none; the editor prevents both, and the server is the contract.
   */
  setGrants: (id: number, grants: readonly GrantInput[]): Promise<Role> =>
    request<Role>(`/api/admin/roles/${String(id)}/grants`, {
      method: 'PUT',
      body: JSON.stringify({ grants }),
    }),

  reports: (): Promise<AdminReportRow[]> => request<AdminReportRow[]>('/api/admin/reports'),

  reloadReports: (): Promise<ReloadResult> =>
    request<ReloadResult>('/api/admin/reports/reload', { method: 'POST' }),

  /** Every department, active or not, with report and grant counts (§10, M8). */
  departments: (): Promise<DepartmentRow[]> =>
    request<DepartmentRow[]>('/api/admin/departments'),

  createDepartment: (payload: DepartmentCreate): Promise<DepartmentRow> =>
    request<DepartmentRow>('/api/admin/departments', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  patchDepartment: (id: number, patch: DepartmentPatch): Promise<DepartmentRow> =>
    request<DepartmentRow>(`/api/admin/departments/${String(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteDepartment: async (id: number): Promise<void> => {
    // 204 → undefined. The server refuses (409) a department still holding
    // reports or grants; the UI disables the button, and the server is the
    // contract (§7.1).
    await request<undefined>(`/api/admin/departments/${String(id)}`, { method: 'DELETE' })
  },

  /**
   * Reassign a report to another department (§7.1, M8). Returns the refreshed
   * department list (its counts move). The server clears any `selected` grant
   * that named the report under its old department and bumps `authz_version`,
   * so callers invalidate the effective-access panels afterwards.
   */
  moveReport: (slug: string, department: string): Promise<DepartmentRow[]> =>
    request<DepartmentRow[]>(`/api/admin/reports/${encodeURIComponent(slug)}/department`, {
      method: 'PUT',
      body: JSON.stringify({ department }),
    }),

  setReportActive: (slug: string, isActive: boolean): Promise<AdminReportRow[]> =>
    request<AdminReportRow[]>(`/api/admin/reports/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive }),
    }),

  audit: (filters: AuditFilters = {}): Promise<AuditPage> =>
    request<AuditPage>(`/api/admin/audit${query({ ...filters })}`),

  cache: (): Promise<CacheStats> => request<CacheStats>('/api/admin/cache'),

  purgeCache: (target: { slug: string } | { all: true }): Promise<CachePurgeResult> =>
    request<CachePurgeResult>('/api/admin/cache/purge', {
      method: 'POST',
      body: JSON.stringify(target),
    }),
}
